import { CompareCard } from '@folio/components/module-elements/CompareCard';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import {
  useHasPolicyProductConfig,
  usePolicyProductConfig,
} from '@folio/utils/usePolicyProductConfig';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Customer,
  keyValueFieldsToObject,
  viewSpecificControllerListPolicyCustomersQueryOptions,
  type ListPolicyCustomersOutput,
} from 'korint-sdk';

type CustomerCompareModuleProps = {
  /**
   * The branches to compare. If undefined, the base branch will be used.
   */
  branches: Array<string | undefined>;
};

export function CustomerCompareModule({ branches }: CustomerCompareModuleProps) {
  const { t } = useTranslation();
  const { policy } = usePolicyOnDashboard();

  const hasCustomerInformations = useHasPolicyProductConfig('customerInformations');
  if (!hasCustomerInformations) {
    return null;
  }

  const customerInformations = usePolicyProductConfig('customerInformations');

  const results = useQueries({
    queries: branches.map(branchId => ({
      ...viewSpecificControllerListPolicyCustomersQueryOptions(policy.id, { branchId }),
      select: (data: ListPolicyCustomersOutput): Customer[] =>
        data.data.map(c => ({
          ...c,
          customFields: keyValueFieldsToObject(c.customFields ?? []),
        })),
    })),
  });

  const isLoading = results.some(result => result.status === 'pending');

  // Collect all unique roles across all branches
  const allRoles = Array.from(new Set(results.flatMap(r => r.data?.map(c => c.role) ?? [])));

  return (
    <>
      {allRoles.map(role => {
        const customers = results.map(result => result.data?.find(c => c.role === role));
        const title = t([
          `${policy.productId}.customerCompareModule.title`,
          'customerCompareModule.title',
        ]);

        return (
          <CompareCard
            key={role}
            title={role ? `${title} : ${t(`relationTypes.${role}.label`)}` : title}
            productId={policy.productId}
            fieldsConfig={customerInformations.fieldsConfig}
            entityType="customer"
            entities={customers}
            isLoading={isLoading}
          />
        );
      })}
    </>
  );
}
